import React, { useEffect } from 'react';
import { useActivation } from '@context/MobileOne';
import { useA11y } from '@context/Utils/A11y';
import { OverviewData } from './OverviewData';
import { PDFDownload } from './PDFDownload';
import { OverviewSuccess } from './OverviewSuccess';
import { OverviewConsent } from './OverviewConsent';
import { OverviewError } from './OverviewError';
import { EditAddressData, EditMnpData, EditPrivateData, EditLegitimation } from './EditData';
import { Meta } from '@core/Meta';
import { keyValueValidation } from '@utils/globalConstant';
import { useTitle } from '@context/Utils/Title';

export function ActivationStep8() {
  // Context
  const { step8SubStep, setShowActivationFooter, setShowActivationHeader } = useActivation();
  const { tA11yTranslate, getGlobalMetaTitles } = useA11y();
  const { announceTitle } = useTitle();
  // META: page titles
  const { globalPageTitle, globalSuccessPageTitle, globalErrorPageTitle } = getGlobalMetaTitles();
  const activationOverviewPageTitle = tA11yTranslate('ek_global_meta_title_overview').content;
  const activationOverviewConsentPageTitle = tA11yTranslate(
    'ek_global_meta_title_overview_consent'
  ).content;
  const activationOverviewConsentDetailsPageTitle = tA11yTranslate(
    'ek_global_meta_title_overview_consent_details'
  ).content;
  const changeMnpPageTitle = tA11yTranslate('ek_global_meta_title_change_mnp').content;
  const changePersonalDataPageTitle = tA11yTranslate(
    'ek_global_meta_title_change_pers_data'
  ).content;
  const changePersonalDataAddressPageTitle = tA11yTranslate(
    'ek_global_meta_title_change_address'
  ).content;
  const changeLegitimatonPageTitle = tA11yTranslate(
    'ek_global_meta_title_change_legitmaton'
  ).content;
  const isTranslationReady = keyValueValidation(
    activationOverviewPageTitle,
    'ek_global_meta_title_overview'
  );

  // Determine the page title based on the current substep
  const getPageTitle = () => {
    if (step8SubStep === 1) {
      return `${globalPageTitle} - ${activationOverviewConsentPageTitle}`;
    } else if (step8SubStep === 2) {
      return `${globalPageTitle} - ${activationOverviewConsentDetailsPageTitle}`;
    } else if (step8SubStep === 3) {
      return `${globalPageTitle} - ${globalSuccessPageTitle}`;
    } else if (step8SubStep === 4) {
      return `${globalPageTitle} - ${globalErrorPageTitle}`;
    } else if (step8SubStep === 5) {
      return `${globalPageTitle} - ${changeMnpPageTitle}`;
    } else if (step8SubStep === 6) {
      return `${globalPageTitle} - ${changePersonalDataPageTitle}`;
    } else if (step8SubStep === 7) {
      return `${globalPageTitle} - ${changePersonalDataAddressPageTitle}`;
    } else if (step8SubStep === 8) {
      return `${globalPageTitle} - ${changeLegitimatonPageTitle}`;
    } else {
      return `${globalPageTitle} - ${activationOverviewPageTitle}`;
    }
  };

  useEffect(() => {
    const pageTitle = `${getPageTitle()}`;
    announceTitle(pageTitle);
  }, [step8SubStep, announceTitle]); // Re-run when substep changes

  useEffect(() => {
    setShowActivationFooter(true);
    setShowActivationHeader(true);

    if (step8SubStep === 2 || step8SubStep === 3 || step8SubStep === 4) {
      setShowActivationFooter(false);
      setShowActivationHeader(false);
    }
  }, [step8SubStep]);

  const renderStep8SubSteps = (subStep) => {
    switch (subStep) {
      case 0:
        return <OverviewData />;
      case 1:
        return <OverviewConsent />;
      case 2:
        // component name reflects former version of the page
        // in FIGMA page is: 0.1.9.2.2 - registration step 8 - legal
        return <PDFDownload />;
      case 3:
        return <OverviewSuccess />;
      case 4:
        return <OverviewError />;
      case 5:
        return <EditMnpData />;
      case 6:
        return <EditPrivateData />;
      case 7:
        return <EditAddressData />;
      case 8:
        return <EditLegitimation />;
      default:
        return <OverviewData />;
    }
  };

  return (
    <>
      <Meta title={getPageTitle()} key={`meta-key-${step8SubStep}`} />
      {renderStep8SubSteps(step8SubStep)}
    </>
  );
}

export default ActivationStep8;
